import { Dispatch, ReactNode, SetStateAction, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import './customer-list.scss';
import classNames from 'classnames';
import Icon from '../../../components/icons/icon';
import { Button } from '../../../components/form/button';
import { Dropdown } from '../../../components/dropdown/dropdown';
import { Typography } from '../../../components/typography/typography';
import { SearchBar } from '../../../components/search-bar/search-bar';
import { PaginationData } from '../../../clients/quipli-service/pagination/pagination';
import { DataTable, DataTableColumn, DataTableRow } from '../../../components/table/data-table';
import { User, UserSearchRequest, UserStatus } from '../../../clients/quipli-service/user/user';
import { QuipliLink } from '../../../components/link/quipli-link';
import { Action } from '../../../components/actions/actions';
import { DateTime } from 'luxon';
import { UserForm } from '../components/user-form';
import { Badge } from '../../../components/badge/badge';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import { toast } from '../../../util/toasts';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { useSearchParams } from 'react-router-dom';
import { Modal } from '../../../components/modal/modal';
import { UserSearch } from '../../../components/user-search/user-search';
import { formatPhoneNumber } from '../../../util/util';

export const CustomerList = () => {
  const { searchUsers, updateUser, deleteUser, restoreUser, mergeUsers } = useAPI();

  // States
  const [isListLoading, setListLoading] = useState<boolean>(true);
  const [data, setData] = useState<DataTableRow[]>([]);
  const [pagination, setPagination] = useState<PaginationData>();
  const [isDeletedUsers, setIsDeletedUsers] = useState<boolean>(false);
  const [isCustomerModalOpen, openCustomerModal] = useState<boolean>(false);

  // List request hooks
  const [params] = useSearchParams();
  const { request, setRequest, fetchCallback } = useRequestQueryParams<UserSearchRequest>(
    {
      keyword: params.has('keyword') ? params.get('keyword')! : undefined,
      isDeleted: params.has('isDeleted') ? params.get('isDeleted')! === 'true' : undefined,
      statusFilter: params.has('statusFilter') ? (params.get('statusFilter')! as UserStatus) : undefined,
      pageSize: params.has('pageSize') ? parseInt(params.get('pageSize')!) : 10,
      pageNumber: params.has('pageNumber') ? parseInt(params.get('pageNumber')!) : 1,
    },
    (_request) => {
      setListLoading(true);
      searchUsers(_request)
        .then(({ items, pagination }) => {
          setPagination(pagination);
          setData(items.map(buildDataRow));
          setListLoading(false);
        })
        .catch(() => {
          setListLoading(false);
          setData([]);
        });
    },
  );

  // Vars
  const columns: DataTableColumn[] = [
    { key: 'name', label: 'Customer Details' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'type', label: 'Customer Type' },
    { key: 'added', label: 'Added On' },
    { key: 'status', label: 'Status' },
    { key: 'actions', label: '', fixedAsLast: true },
  ];

  const buildDataRow = (user: User) => {
    return {
      mobileHeader: (
        <>
          <Typography type="text-md" weight="medium">
            {user.name}
          </Typography>
          <Typography type="text-sm" weight="regular" className="inline text-[var(--grey-500)]">
            {user.email}
          </Typography>
        </>
      ),
      fields: {
        name: (
          <>
            <div>
              <Typography
                type="text-sm"
                className={user.status === UserStatus.Active ? 'text-quipli-black-800' : 'text-grey-400'}
              >
                {user.name}
              </Typography>
            </div>
          </>
        ),
        email: (
          <>
            <div>
              <Typography
                type="text-sm"
                className={user.status === UserStatus.Active ? 'text-quipli-black-800' : 'text-grey-400'}
              >
                {user.email}
              </Typography>
            </div>
          </>
        ),
        phone: (
          <>
            <div>
              <Typography
                type="text-sm"
                className={user.status === UserStatus.Active ? 'text-quipli-black-800' : 'text-grey-400'}
              >
                {user.phone.trim() == '' ? '-' : formatPhoneNumber(user.phone)}
              </Typography>
            </div>
          </>
        ),
        type: (
          <>
            <div>
              <Typography
                type="text-sm"
                className={user.status === UserStatus.Active ? 'text-quipli-black-800' : 'text-grey-400'}
              >
                {user.type}
              </Typography>
            </div>
          </>
        ),
        added: (
          <>
            <div>
              <Typography
                type="text-sm"
                className={user.status === UserStatus.Active ? 'text-quipli-black-800' : 'text-grey-400'}
              >
                {DateTime.fromISO(user.addedOn, { setZone: true }).toFormat('LLL-dd')}
              </Typography>
              <Typography
                type="text-sm"
                className={user.status === UserStatus.Active ? 'text-grey-500' : 'text-grey-400'}
              >
                {DateTime.fromISO(user.addedOn, { setZone: true }).toFormat('hh:mm a').toLowerCase()}
              </Typography>
            </div>
          </>
        ),
        status: (
          <>
            <Badge text={user.status} type={user.status === UserStatus.Active ? 'success' : 'default'} />
          </>
        ),
        actions: (
          <Actions
            user={user}
            doUpdate={doUpdate}
            doMerge={async (id: number, mergeToId: number) => {
              await mergeUsers(mergeToId, id);
              fetchCallback();
            }}
          />
        ),
      },
      redirectTo: `users/customer_form/${user.id}`,
    };
  };

  async function doUpdate(id: number, type: string, update: User) {
    if (type == 'delete') {
      await deleteUser(id);
    } else if (type == 'restore') {
      await restoreUser(id);
    } else {
      await updateUser(update);
    }

    fetchCallback();
  }

  return (
    <div className="customer-list">
      <div className="container-v2">
        <div className="flex justify-between items-center">
          <Typography type="display-sm" weight="medium">
            Manage Customers
          </Typography>

          <Options
            className="block md:hidden"
            setRequest={setRequest}
            isDeletedUsers={isDeletedUsers}
            setIsDeletedUsers={setIsDeletedUsers}
            openCustomerModal={openCustomerModal}
          />
        </div>

        <div className="pt-10 pb-5 flex items-center justify-between">
          <SearchBar
            data-cy="search-customers"
            placeholder="Search by keyword"
            defaultValue={request.keyword}
            setRequest={setRequest}
          />

          <Options
            className="hidden md:block"
            setRequest={setRequest}
            isDeletedUsers={isDeletedUsers}
            setIsDeletedUsers={setIsDeletedUsers}
            openCustomerModal={openCustomerModal}
          />
        </div>

        <Filters request={request} setRequest={setRequest} />

        <DataTable
          isLoading={isListLoading}
          columnNames={columns}
          data={data}
          pagination={pagination}
          request={request}
          setRequest={setRequest}
          cypressPrevious="previous_customer_page"
          cypressNext="next_customer_page"
        />
      </div>
      <UserForm
        isOpen={isCustomerModalOpen}
        close={() => {
          openCustomerModal(false);
          setRequest((prevState) => ({ ...prevState }));
        }}
      />
    </div>
  );
};

const Filters = ({
  request,
  setRequest,
}: {
  request: UserSearchRequest;
  setRequest: Dispatch<SetStateAction<UserSearchRequest>>;
}) => {
  return (
    <div>
      <div className="flex items-center justify-start mb-2">
        <div className={classNames('relative', 'block', 'filter')}>
          <Dropdown
            trigger={(isOpen) => (
              <Button className="button-tertiary">
                <Typography type="text-sm" className="text-quipli-black-800">
                  Status
                </Typography>
                {isOpen ? <Icon icon="ChevronUp" size="small" /> : <Icon icon="ChevronDown" size="small" />}
              </Button>
            )}
          >
            <div>
              <div
                className="flex gap-2"
                onClick={() => {
                  setRequest((prevState) => ({ ...prevState, statusFilter: UserStatus.Active }));
                }}
              >
                <Typography type="text-sm">Active</Typography>
              </div>
            </div>
            <div>
              <div
                className="flex gap-2"
                onClick={() => {
                  setRequest((prevState) => ({ ...prevState, statusFilter: UserStatus.Inactive }));
                }}
              >
                <Typography type="text-sm">Inactive</Typography>
              </div>
            </div>
          </Dropdown>
        </div>
      </div>

      {request.statusFilter !== undefined && (
        <div className="pb-2 flex flex-col space-y-2">
          <div className="flex space-x-2">
            <div className="badge flex items-center space-x-1 px-2 bg-gray-200 text-gray-800 rounded-full">
              <Typography type="text-sm" className="text-quipli-grey-700">
                {request.statusFilter == UserStatus.Active ? 'Active' : 'Inactive'}
              </Typography>
              <Action
                onClick={() => {
                  setRequest((prevState) => ({ ...prevState, statusFilter: undefined }));
                }}
                icon={<Icon size="small" icon="X" />}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Options = ({
  className,
  setRequest,
  isDeletedUsers,
  setIsDeletedUsers,
  openCustomerModal,
}: {
  className?: string;
  setRequest: Dispatch<SetStateAction<UserSearchRequest>>;
  isDeletedUsers?: boolean;
  setIsDeletedUsers: Dispatch<SetStateAction<boolean>>;
  openCustomerModal: Dispatch<SetStateAction<boolean>>;
}) => {
  return (
    <div className={classNames('relative', className)}>
      <Dropdown
        trigger={() => (
          <Button className="button-tertiary" data-cy="customer_options">
            <Icon icon="DotsVertical" />
          </Button>
        )}
      >
        <div className="flex gap-2" data-cy="add_customer" onClick={() => openCustomerModal(true)}>
          <Icon icon="AddCustomer" />
          <Typography type="text-sm" className="text-quipli-black-800">
            Add new customer
          </Typography>
        </div>
        <QuipliLink to={'users/import'} data-cy="add_users">
          <div className="flex gap-2">
            <Icon icon="AddMultipleCustomer" />
            <Typography type="text-sm" className="text-quipli-black-800">
              Bulk upload
            </Typography>
          </div>
        </QuipliLink>
        <div>
          <div
            className="flex gap-2"
            onClick={() => {
              setRequest((prevState) => ({
                isDeleted: !isDeletedUsers,
                keyword: prevState.keyword,
                statusFilter: prevState.statusFilter,
              }));
              setIsDeletedUsers(!isDeletedUsers);
            }}
            data-cy="delete_users"
          >
            <Icon
              icon={isDeletedUsers ? 'CheckCircleBroken' : 'Trash'}
              stroke={isDeletedUsers ? 'var(--success-500)' : 'var(--error-500)'}
            />
            <Typography type="text-sm" className={isDeletedUsers ? 'text-success-500' : 'text-error-500'}>
              {isDeletedUsers ? 'Active' : 'Delete'} users
            </Typography>
          </div>
        </div>
      </Dropdown>
    </div>
  );
};

const Actions = ({
  user,
  doUpdate,
  doMerge,
}: {
  user: User;
  doUpdate: (id: number, type: string, update: User) => void;
  doMerge: (id: number, mergeToId: number) => Promise<void>;
}) => {
  const [modal, setModal] = useState<ReactNode | undefined>();

  return (
    <div>
      {user.isDeleted ? (
        <Dropdown
          placement="left-end"
          trigger={() => (
            <Button className="button-tertiary" data-cy={`customer_menu`}>
              <Icon icon="DotsVertical" />
            </Button>
          )}
        >
          <div>
            <div
              className="flex gap-2"
              onClick={() => {
                doUpdate(user.id, 'restore', { ...user, isDeleted: false });
              }}
              data-cy="restore_customer"
            >
              <Icon icon="CheckCircle" stroke="var(--grey-500)" />
              <Typography type="text-sm" className="text-quipli-black-800">
                Restore customer
              </Typography>
            </div>
          </div>
        </Dropdown>
      ) : (
        <Dropdown
          placement="left-end"
          trigger={() => (
            <Button className="button-tertiary" data-cy={`customer_menu`}>
              <Icon icon="DotsVertical" />
            </Button>
          )}
        >
          <div>
            {user.status == 'Active' ? (
              <div
                className="flex gap-2"
                onClick={() => {
                  doUpdate(user.id, '', { ...user, status: UserStatus.Inactive });
                }}
                data-cy="disable_customer"
              >
                <Icon icon="CheckCircle" stroke="var(--grey-500)" />
                <Typography type="text-sm" className="text-quipli-black-800">
                  Disable customer
                </Typography>
              </div>
            ) : (
              <div
                className="flex gap-2"
                onClick={() => {
                  doUpdate(user.id, '', { ...user, status: UserStatus.Active });
                }}
                data-cy="enable_customer"
              >
                <Icon icon="CheckCircle" stroke="var(--success-500)" />
                <Typography type="text-sm" className="text-quipli-black-800">
                  Enable customer
                </Typography>
              </div>
            )}
          </div>
          <QuipliLink to={`users/customer_form/${user.id}`} data-cy="edit_customer">
            <div className="flex gap-2">
              <Icon icon="Edit" />
              <Typography type="text-sm" className="text-quipli-black-800">
                Edit Details
              </Typography>
            </div>
          </QuipliLink>

          {user.type != 'Guest' && (
            <QuipliLink to={`users/login/${user.id}`} data-cy="login_as_customer">
              <div className="flex gap-2">
                <Icon icon="LogIn04" />
                <Typography type="text-sm" className="text-quipli-black-800">
                  Login into store
                </Typography>
              </div>
            </QuipliLink>
          )}

          <div>
            <div
              className="flex gap-2"
              onClick={() =>
                setModal(<MergeModal user={user} onSubmit={doMerge} onClose={() => setModal(undefined)} />)
              }
              data-cy="merge_customer"
            >
              <Icon icon="Merge" />
              <Typography type="text-sm" className="text-quipli-black-800">
                Merge Customer
              </Typography>
            </div>
          </div>

          <div>
            <div
              className="flex gap-2"
              onClick={() =>
                setModal(
                  <ConfirmModal
                    prompt="Delete customer?"
                    additionalText="This cannot be undone"
                    noLabel="Cancel"
                    yesLabel="Delete"
                    yesButtonYellow
                    isOpen={true}
                    headerIcon={
                      <div className="flex justify-center">
                        <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
                      </div>
                    }
                    onConfirm={() => {
                      doUpdate(user.id, 'delete', { ...user, isDeleted: true });
                      toast('Customer deleted.', 'success');
                      setModal(undefined);
                    }}
                    close={() => setModal(undefined)}
                  />,
                )
              }
              data-cy="delete_customer"
            >
              <Icon icon="Trash" stroke="var(--error-500)" />
              <Typography type="text-sm" className="text-error-500">
                Delete customer
              </Typography>
            </div>
          </div>
        </Dropdown>
      )}

      {modal}
    </div>
  );
};

const MergeModal = ({
  user,
  onSubmit,
  onClose,
}: {
  user: User;
  onSubmit: (id: number, mergeTo: number) => Promise<void>;
  onClose: () => void;
}) => {
  const [isMerging, setIsMerging] = useState<boolean>(false);
  const [mergeTo, setMergeTo] = useState<User | undefined>();

  return (
    <Modal
      title="Merge Customer"
      isOpen={true}
      headerIcon={
        <div className="flex justify-center">
          <Icon icon="Warning" size="xlarge" stroke="var(--warning-500)" />
        </div>
      }
      additionalText={
        <>
          Select a customer to merge with. <br />
          Merging customers will combine all customer data into one account. <br /> This action cannot be undone.
        </>
      }
      close={onClose}
      actions={
        <>
          <Button className="button-secondary" disabled={isMerging} onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="button-yellow"
            disabled={!mergeTo || isMerging}
            onClick={async () => {
              if (!mergeTo) return;

              setIsMerging(true);
              await onSubmit(user.id, mergeTo.id);
              toast('Customer merged.', 'success');
              setIsMerging(false);
              setMergeTo(undefined);
              onClose();
            }}
          >
            Merge
          </Button>
        </>
      }
    >
      <UserSearch
        locations={[]}
        placeholder="Search by email"
        searchBy="email"
        excludeUserIds={[user.id]}
        selectedUser={mergeTo}
        onSelectUser={setMergeTo}
        formatSelected={(user) => (
          <Typography type="text-sm" className="text-quipli-black-800">
            {user.name} <span className="!text-grey-500">({user.email})</span>
          </Typography>
        )}
        formatOptions={(user) => (
          <div className="flex flex-col gap-2">
            <Typography type="text-sm">{user.name}</Typography>
            <Typography type="text-sm" className="text-grey-500">
              {user.email}
            </Typography>
          </div>
        )}
      />
    </Modal>
  );
};
